/**
 * Translation dictionaries.
 *
 * Enum labels live under `enum.<set>.<value>` and are resolved generically by the
 * registry — so a new status added to `@ias/shared` surfaces as a select option, a
 * filter chip and a badge everywhere at once, and the only edit needed here is the
 * label itself. No screen ever spells out an enum value.
 */

export const dictionaries = {
  el: {
    'app.name': 'Orion',
    'app.shortName': 'Orion',
    'app.tagline': 'Insurance OS',

    'nav.today': 'Η μέρα μου',
    'nav.dashboard': 'Επισκόπηση',
    'nav.collections': 'Εισπράξεις',
    'nav.notices': 'Ειδοποιητήρια',
    'nav.clients': 'Πελάτες',
    'nav.policies': 'Συμβόλαια',
    'nav.renewals': 'Ανανεώσεις',
    'nav.claims': 'Ζημιές',
    'nav.reminders': 'Υπενθυμίσεις',
    'nav.commissions': 'Προμήθειες',
    'nav.reports': 'Αναφορές',
    'nav.settings': 'Ρυθμίσεις',
    'nav.users': 'Χρήστες',
    'nav.companies': 'Ασφαλιστικές',
    'nav.audit': 'Ιστορικό ενεργειών',
    'nav.import': 'Εισαγωγή δεδομένων',
    'nav.groupMain': 'Καθημερινά',
    'nav.groupInsight': 'Ανάλυση',
    'nav.groupAdmin': 'Διαχείριση',
    'nav.collapse': 'Σύμπτυξη',

    'action.create': 'Δημιουργία',
    'action.edit': 'Επεξεργασία',
    'action.save': 'Αποθήκευση',
    'action.cancel': 'Ακύρωση',
    'action.delete': 'Διαγραφή',
    'action.close': 'Κλείσιμο',
    'action.confirm': 'Επιβεβαίωση',
    'action.search': 'Αναζήτηση',
    'action.filter': 'Φίλτρα',
    'action.clear': 'Καθαρισμός',
    'action.clearAll': 'Καθαρισμός όλων',
    'action.export': 'Εξαγωγή',
    'action.import': 'Εισαγωγή',
    'action.view': 'Προβολή',
    'action.back': 'Πίσω',
    'action.add': 'Προσθήκη',
    'action.upload': 'Μεταφόρτωση',
    'action.download': 'Λήψη',
    'action.retry': 'Δοκιμή ξανά',
    'action.markPaid': 'Εξόφληση',
    // The same dialog, named for what it actually does when there is nothing left to collect:
    // a policy stays «εξοφλημένο» after every renewal, so «Εξόφληση» beside a «Εξοφλημένο»
    // badge read as an action already taken.
    'action.renewPeriod': 'Ανανέωση',
    'action.renew': 'Ανανέωση',
    'action.markComplete': 'Ολοκλήρωση',
    'action.reopen': 'Επαναφορά',
    'action.assign': 'Ανάθεση',
    'action.more': 'Περισσότερα',
    'action.selectAll': 'Επιλογή όλων',
    'action.copy': 'Αντιγραφή',
    'action.newPolicy': 'Νέο συμβόλαιο',
    'action.newClaim': 'Νέα ζημιά',
    'action.newReminder': 'Νέα υπενθύμιση',
    'action.newClient': 'Νέος πελάτης',

    'common.loading': 'Φόρτωση…',
    'common.noResults': 'Δεν βρέθηκαν αποτελέσματα',
    'common.noResultsHint': 'Δοκιμάστε να αλλάξετε ή να καθαρίσετε τα φίλτρα.',
    'common.empty': 'Δεν υπάρχουν εγγραφές ακόμη',
    'common.error': 'Παρουσιάστηκε σφάλμα',
    'common.all': 'Όλα',
    'common.none': 'Κανένα',
    'common.yes': 'Ναι',
    'common.no': 'Όχι',
    'common.total': 'Σύνολο',
    'common.of': 'από',
    'common.rows': 'εγγραφές',
    'common.rowsPerPage': 'Ανά σελίδα',
    'common.page': 'Σελίδα',
    'common.selected': 'επιλεγμένα',
    'common.required': 'Υποχρεωτικό πεδίο',
    'error.invalidInput': 'Μη έγκυρη τιμή',
    'error.invalidNumber': 'Αναμένεται αριθμός',
    'common.optional': 'προαιρετικό',
    'common.unassigned': 'Χωρίς ανάθεση',
    'common.never': 'Ποτέ',
    'common.today': 'Σήμερα',
    'common.days': 'ημέρες',
    // Short form for chips. Note: NOT the first letter of «ημέρες» — «1η» reads as
    // the ordinal "1st" in Greek, which is a different statement entirely.
    'common.daysShort': 'ημ.',
    'common.daysLeft': 'ημέρες απομένουν',
    'common.daysOverdue': 'ημέρες καθυστέρηση',
    'common.expired': 'Έληξε',
    'common.from': 'Από',
    'common.to': 'Έως',
    'common.dateRange': 'Χρονικό διάστημα',
    'common.density': 'Πυκνότητα',
    'common.columns': 'Στήλες',
    'common.actions': 'Ενέργειες',
    'common.details': 'Στοιχεία',
    'common.history': 'Ιστορικό',
    'common.documents': 'Έγγραφα',
    'common.notes': 'Σημειώσεις',
    'common.createdAt': 'Δημιουργήθηκε',
    'common.updatedAt': 'Ενημερώθηκε',
    'common.createdBy': 'Από',
    'common.saved': 'Αποθηκεύτηκε',
    'common.deleted': 'Διαγράφηκε',
    /* Count-aware forms. Resolved by `tn(key, n)` — see lib/i18n. */
    'count.selected.one': '{n} επιλεγμένο',
    'count.selected.other': '{n} επιλεγμένα',
    'count.rows.one': '{n} εγγραφή',
    'count.rows.other': '{n} εγγραφές',
    'count.updated.one': '{n} εγγραφή ενημερώθηκε',
    'count.updated.other': '{n} εγγραφές ενημερώθηκαν',
    'table.footerBasis.one': 'Σύνολο για {n} εγγραφή του φίλτρου',
    'table.footerBasis.other': 'Σύνολο για {n} εγγραφές του φίλτρου',
    'count.policies.one': '{n} συμβόλαιο',
    'count.policies.other': '{n} συμβόλαια',

    'auth.signIn': 'Σύνδεση',
    'auth.signOut': 'Αποσύνδεση',
    'auth.email': 'Email',
    'auth.password': 'Κωδικός',
    'auth.rememberMe': 'Να με θυμάσαι',
    'auth.welcome': 'Καλώς ήρθατε',
    'auth.subtitle': 'Συνδεθείτε για να συνεχίσετε.',
    'auth.brandLine': 'Φτιαγμένο για ασφαλιστές.',
    'auth.demoHint': 'Δοκιμαστικοί λογαριασμοί',
    'auth.changePassword': 'Αλλαγή κωδικού',
    'auth.currentPassword': 'Τρέχων κωδικός',
    'auth.newPassword': 'Νέος κωδικός',
    'auth.profile': 'Ο λογαριασμός μου',
    'auth.sessionExpired': 'Η συνεδρία έληξε. Συνδεθείτε ξανά.',
    'dialog.newClientHint': 'Στοιχεία επικοινωνίας και φορολογικά — τα υπόλοιπα συμπληρώνονται αργότερα',
    'dialog.editClientHint': 'Αλλαγές στα στοιχεία του πελάτη',
    'dialog.newPolicyHint': 'Κάλυψη, διάρκεια και ασφάλιστρο — οι δόσεις υπολογίζονται αυτόματα',
    'dialog.newClaimHint': 'Καταχώρηση περιστατικού· ο αριθμός της ασφαλιστικής μπορεί να μπει αργότερα',
    'dialog.editClaimHint': 'Αλλαγές στα στοιχεία της ζημιάς',
    'auth.changePasswordHint': 'Τουλάχιστον 10 χαρακτήρες, με πεζά, κεφαλαία και αριθμό',
    'auth.forgotPassword': 'Ξεχάσατε τον κωδικό σας;',
    'auth.forgotPasswordHint': 'Γράψτε το email του λογαριασμού σας και θα σας στείλουμε σύνδεσμο για νέο κωδικό.',
    'auth.sendResetLink': 'Αποστολή συνδέσμου',
    'auth.resetLinkSent': 'Αν υπάρχει λογαριασμός με αυτό το email, στάλθηκε σύνδεσμος επαναφοράς. Ισχύει για 60 λεπτά — ελέγξτε και τα ανεπιθύμητα.',
    'auth.backToSignIn': 'Επιστροφή στη σύνδεση',
    'auth.resetPasswordTitle': 'Νέος κωδικός',
    'auth.resetPasswordHint': 'Ορίστε νέο κωδικό για τον λογαριασμό σας. Όλες οι συνδεδεμένες συσκευές θα αποσυνδεθούν.',
    'auth.confirmPassword': 'Επιβεβαίωση κωδικού',
    'auth.passwordsDoNotMatch': 'Οι κωδικοί δεν ταιριάζουν',
    'auth.resetDone': 'Ο κωδικός άλλαξε. Συνδεθείτε με τον νέο κωδικό.',
    'auth.resetLinkMissing': 'Ο σύνδεσμος δεν είναι πλήρης. Ανοίξτε τον ξανά από το email ή ζητήστε νέο.',
    'auth.requestNewLink': 'Ζητήστε νέο σύνδεσμο',
    'document.dropzone': 'Σύρετε αρχείο εδώ ή επιλέξτε',
    'confirm.cannotUndo': 'Η ενέργεια δεν αναιρείται.',
    'confirm.deleteDocument': 'Να διαγραφεί το έγγραφο;',
    'confirm.discardChange': 'Να απορριφθεί η αλλαγή;',
    'confirm.discardChangeHint':
      'Η αλλαγή καταγράφηκε χωρίς σύνδεση και δεν έχει σταλεί ακόμη. Θα χαθεί οριστικά.',
    'document.allTypes': 'Όλα τα είδη',
    'document.filterByType': 'Φιλτράρισμα ανά είδος',
    'document.preview': 'Προεπισκόπηση',
    'document.previewUnsupported': 'Δεν γίνεται προεπισκόπηση αυτού του τύπου αρχείου',
    'document.previewNeedsConnection': 'Η προεπισκόπηση κατεβάζει το αρχείο για να το δείξει, οπότε χρειάζεται σύνδεση',

    'dashboard.title': 'Επισκόπηση',
    'dashboard.subtitle':
      'Τα ασφάλιστρα και οι προμήθειες αφορούν την επιλεγμένη περίοδο — τα υπόλοιπα μεγέθη δείχνουν την τρέχουσα εικόνα του χαρτοφυλακίου',
    'dashboard.period': 'Περίοδος',
    'dashboard.period.30d': 'Τελευταίες 30 ημέρες',
    'dashboard.period.90d': 'Τελευταίες 90 ημέρες',
    'dashboard.period.365d': 'Τελευταίοι 12 μήνες',
    'dashboard.period.all': 'Όλο το διάστημα',
    'dashboard.period.custom': 'Προσαρμοσμένο',
    'dashboard.writtenFrom': 'Έναρξη συμβολαίου από',
    'dashboard.writtenTo': 'Έναρξη συμβολαίου έως',
    'dashboard.totalPremium': 'Ασφάλιστρα',
    'dashboard.totalCommission': 'Προμήθειες',
    'dashboard.activePolicies': 'Ενεργά συμβόλαια',
    'dashboard.upcomingRenewals': 'Επερχόμενες ανανεώσεις',
    'dashboard.unpaidPolicies': 'Ανεξόφλητα',
    'dashboard.unpaidPoliciesHint': 'Συμβόλαια με ανοιχτό υπόλοιπο — χωρίς τα ακυρωμένα και τα ληγμένα',
    'dashboard.receivablesBasis': 'σε δόσεις',
    'dashboard.openClaims': 'Ανοιχτές ζημιές',
    'dashboard.averagePremium': 'Μέσο ασφάλιστρο',
    'dashboard.receivables': 'Προς είσπραξη',
    'dashboard.unreconciled': 'Προμήθειες προς εκκαθάριση',
    'dashboard.unreconciledHint': 'Εισπραγμένα συμβόλαια που δεν έχουν ακόμη περιληφθεί σε εκκαθάριση ασφαλιστικής',
    'dashboard.retention': 'Διατήρηση πελατολογίου',
    'dashboard.premiumByMonth': 'Ασφάλιστρα & προμήθειες ανά μήνα',
    'dashboard.premiumByMonthHint': 'Η προμήθεια του γραφείου ως μέρος του συνολικού ασφαλίστρου',
    'dashboard.byType': 'Κατανομή ανά κλάδο',
    'dashboard.byTypeHint': 'Ενεργά συμβόλαια, κατά ύψος ασφαλίστρων',
    'dashboard.upcomingList': 'Ανανεώσεις επόμενων 30 ημερών',
    'dashboard.overdueList': 'Εκπρόθεσμες υπενθυμίσεις',
    'dashboard.netPremium': 'Καθαρό ασφάλιστρο',
    'dashboard.commission': 'Προμήθεια γραφείου',
    'dashboard.vsPrevious': 'έναντι προηγούμενης περιόδου',
    'dashboard.showTable': 'Προβολή πίνακα',
    'dashboard.showChart': 'Προβολή γραφήματος',

    'client.title': 'Πελάτες',
    'client.subtitle': 'Το πελατολόγιο του γραφείου',
    'client.one': 'Πελάτης',
    'client.fullName': 'Ονοματεπώνυμο / Επωνυμία',
    'client.type': 'Τύπος',
    'client.taxNumber': 'ΑΦΜ',
    'client.idNumber': 'Αρ. ταυτότητας',
    'client.drivingLicense': 'Αρ. διπλώματος',
    'client.dateOfBirth': 'Ημ. γέννησης',
    'client.contactPerson': 'Υπεύθυνος επικοινωνίας',
    'client.phone': 'Τηλέφωνο',
    'client.email': 'Email',
    'client.address': 'Διεύθυνση',
    'client.city': 'Πόλη',
    'client.postalCode': 'Τ.Κ.',
    'client.iban': 'IBAN',
    'client.tags': 'Ετικέτες',
    'client.agent': 'Υπεύθυνος συνεργάτης',
    'client.marketingConsent': 'Συγκατάθεση επικοινωνίας',
    'client.activePolicies': 'Ενεργά συμβόλαια',
    'client.vehicles': 'Οχήματα',
    // The count is every plate the archive holds for this client; the hint says how many of
    // them are still on cover, because «4 οχήματα» alone reads as four cars insured today.
    'client.vehiclesHint': '{active} με ενεργό συμβόλαιο',
    'client.vehiclesNoneActive': 'κανένα ενεργό',
    'vehicle.insured': 'Ασφαλισμένο',
    'vehicle.notInsured': 'Χωρίς ενεργό',
    'client.searchPlateHint': 'Αναζήτηση και με αριθμό κυκλοφορίας',
    'client.activities': 'Επικοινωνία',
    'client.exportData': 'Εξαγωγή δεδομένων (GDPR)',
    'client.newActivity': 'Καταγραφή επικοινωνίας',

    'policy.title': 'Συμβόλαια',
    'policy.subtitle': 'Όλα τα συμβόλαια, με τα τελευταία καταχωρημένα πρώτα',
    'policy.one': 'Συμβόλαιο',
    'policy.number': 'Αρ. συμβολαίου',
    'policy.renewalNumber': 'Αρ. ανανεωτηρίου',
    'policy.renewalNumberHint': 'Προαιρετικό — κάποιες εταιρείες κρατούν τον ίδιο κεντρικό αριθμό',
    'policy.nextRenewalNumber': 'Αρ. νέου ανανεωτηρίου',
    'policy.nextRenewalNumberHint':
      'Ο αριθμός του ανανεωτηρίου που εκδόθηκε για τη νέα περίοδο. Άφησέ το κενό αν η εταιρεία κρατά τον ίδιο κεντρικό αριθμό.',
    'policy.company': 'Ασφαλιστική',
    'policy.insuranceType': 'Κλάδος',
    'policy.startDate': 'Έναρξη',
    'policy.endDate': 'Λήξη',
    'policy.frequency': 'Συχνότητα',
    'policy.premium': 'Ολικά ασφάλιστρα',
    'policy.premiumHint': 'Ό,τι πληρώνει ο πελάτης — με φόρους και επιβαρύνσεις',
    'policy.netPremium': 'Καθαρά ασφάλιστρα',
    'policy.netPremiumHint': 'Πάνω σε αυτά υπολογίζεται η προμήθεια',
    'policy.netPremiumMissing': 'Λείπουν τα καθαρά',
    'policy.netPremiumMissingHint':
      'Χωρίς καθαρά ασφάλιστρα η προμήθεια μένει 0 — δεν υπολογίζεται στα ολικά.',
    'policy.commission': 'Προμήθεια',
    'policy.commissionPct': 'Ποσοστό προμήθειας',
    'policy.specialCommission': 'Ειδική προμήθεια',
    'policy.amountIncomplete': 'Συμπλήρωσε ολόκληρο το ποσό πριν την επιβεβαίωση',
    'policy.previewStale': 'Τα ποσά δεν ξαναϋπολογίστηκαν — έλεγξε το ποσό που πληκτρολόγησες',
    'policy.vehicleCategory': 'Κατηγορία οχήματος',
    'policy.vehicleCategoryHint': 'Καθορίζει το ποσοστό προμήθειας της εταιρείας',
    'policy.status': 'Κατάσταση',
    'policy.paymentStatus': 'Πληρωμή',
    'policy.coverage': 'Καλύψεις',
    'policy.daysUntilExpiry': 'Ημέρες έως λήξη',
    'policy.nextRenewal': 'Επόμενη ανανέωση',
    'policy.renewalHistory': 'Ιστορικό ανανεώσεων',
    'policy.period': 'Περίοδος',
    'policy.installments': 'Δόσεις',
    'policy.installment': 'Δόση',
    'policy.endorsements': 'Πρόσθετες πράξεις',
    'endorsement.new': 'Νέα πρόσθετη πράξη',
    'endorsement.type': 'Είδος',
    'endorsement.number': 'Αρ. πρόσθετης πράξης',
    'endorsement.effectiveDate': 'Ισχύει από',
    'endorsement.description': 'Περιγραφή μεταβολής',
    'endorsement.descriptionHint': 'Τι αλλάζει στο συμβόλαιο — αυτό διαβάζει ο επόμενος συνάδελφος',
    'endorsement.newPremium': 'Νέο ασφάλιστρο',
    'endorsement.newPremiumHint': 'Αφήστε το κενό αν το ασφάλιστρο δεν αλλάζει',
    'endorsement.newCommission': 'Νέο ποσοστό προμήθειας',
    'endorsement.preview': 'Επίδραση στο συμβόλαιο',
    'endorsement.currentPremium': 'Τρέχον ασφάλιστρο',
    'endorsement.delta': 'Διαφορά',
    'endorsement.noChange': 'Χωρίς μεταβολή ασφαλίστρου',
    'endorsement.installmentsNote': 'Οι ανεξόφλητες δόσεις θα αναπροσαρμοστούν στο νέο ασφάλιστρο.',
    'endorsement.saved': 'Η πρόσθετη πράξη καταχωρήθηκε',
    'endorsement.blockedCancelled': 'Δεν εκδίδεται πρόσθετη πράξη σε ακυρωμένο συμβόλαιο',
    'policy.insuredItems': 'Ασφαλισμένα αντικείμενα',
    'policy.plate': 'Αρ. κυκλοφορίας',
    'policy.showNonRenewing': 'Χωρίς ανανέωση',
    'policy.markPaidTitle': 'Εξόφληση & ανανέωση',
    // Same dialog, named for what is left to do when nothing is owed — matching the button
    // that opened it, which already stopped saying «Εξόφληση» beside a «Εξοφλημένο» badge.
    'policy.renewTitle': 'Ανανέωση συμβολαίου',
    'policy.renewOnlyTitle': 'Ανανέωση περιόδου',
    'policy.alreadyCollected':
      'Η περίοδος έχει ήδη εισπραχθεί, οπότε δεν χρεώνεται τίποτα. Η ενέργεια την αρχειοθετεί και προχωράει το συμβόλαιο στην επόμενη περίοδο. Συμπλήρωσε πότε εισπράχθηκε, για να καταγραφεί σωστά στο ιστορικό.',
    'policy.renewalPreview': 'Προεπισκόπηση ανανέωσης',
    'policy.currentPeriod': 'Τρέχουσα περίοδος (θα αρχειοθετηθεί)',
    'policy.nextPeriod': 'Νέα περίοδος',
    'policy.willNotRenew': 'Δεν θα γίνει ανανέωση',
    'policy.cancelTitle': 'Ακύρωση συμβολαίου',
    'action.cancelPolicy': 'Ακύρωση',
    'policy.deleteTitle': 'Διαγραφή συμβολαίου;',
    'policy.deleteHint': 'Για λάθος καταχωρήσεις, π.χ. διπλότυπο. Το συμβόλαιο φεύγει από παντού χωρίς ακυρωτικό· η διαγραφή μένει στο ιστορικό. Αν ο πελάτης σταμάτησε την ασφάλιση, χρησιμοποίησε «Ακύρωση».',
    'policy.deleted': 'Το συμβόλαιο διαγράφηκε',
    'policy.cancelReason': 'Αιτιολογία ακύρωσης',
    'policy.newEndorsement': 'Νέα πρόσθετη πράξη',
    'policy.premiumDelta': 'Μεταβολή ασφαλίστρου',
    'policy.effectiveDate': 'Ημ. ισχύος',
    'policy.dueDate': 'Ημ. πληρωμής',
    'policy.amount': 'Ποσό',
    'policy.paymentMethod': 'Τρόπος πληρωμής',
    'policy.recordPayment': 'Καταχώρηση πληρωμής',
    'policy.bulkAction': 'Μαζική ενέργεια',

    'renewals.title': 'Ανανεώσεις',
    'renewals.subtitle': 'Ο φόρτος ανανεώσεων ανά προτεραιότητα',
    'renewals.critical': 'Κρίσιμα (< 7 ημέρες)',
    'renewals.soon': 'Άμεσα (7–14 ημέρες)',
    'renewals.upcoming': 'Επερχόμενα (15–30 ημέρες)',
    'renewals.lapsed': 'Ληγμένα',
    'renewals.empty': 'Καμία ανανέωση σε αυτή τη ζώνη',
    'renewals.decline': 'Δεν ανανεώνει',
    'renewals.declineReason': 'Λόγος μη ανανέωσης',
    'renewals.declineReasonPlaceholder': 'π.χ. πούλησε το όχημα, πήγε σε άλλο γραφείο',
    'renewals.declineTitle': 'Ο πελάτης δεν θα ανανεώσει;',
    'renewals.declineHint':
      'Το συμβόλαιο φεύγει από τις Ανανεώσεις. Επαναφέρεται από τα Ειδοποιητήρια, με το φίλτρο «Δεν ανανεώνει».',

    'claim.title': 'Ζημιές',
    'claim.subtitle': 'Διαχείριση και παρακολούθηση αποζημιώσεων',
    'claim.one': 'Ζημιά',
    'claim.number': 'Αρ. ζημιάς',
    'claim.incidentDate': 'Ημ. συμβάντος',
    'claim.reportDate': 'Ημ. δήλωσης',
    'claim.amount': 'Αιτούμενο ποσό',
    'claim.payout': 'Αποζημίωση',
    'claim.daysOpen': 'Ημέρες ανοιχτή',
    'claim.timeline': 'Χρονολόγιο',
    'claim.addEntry': 'Νέα καταχώρηση',
    'claim.description': 'Περιγραφή συμβάντος',
    'claim.changeStatus': 'Αλλαγή κατάστασης',
    'claim.systemEntry': 'αυτόματο',
    'claim.statusChangedTo': 'Η κατάσταση άλλαξε από «{from}» σε «{to}»',

    'reminder.title': 'Υπενθυμίσεις',
    'reminder.subtitle': 'Ενέργειες που εκκρεμούν',
    'reminder.one': 'Υπενθύμιση',
    'reminder.date': 'Ημερομηνία',
    'reminder.priority': 'Προτεραιότητα',
    'reminder.assignedTo': 'Ανατέθηκε σε',
    'reminder.relatedTo': 'Σχετίζεται με',
    'reminder.tabAll': 'Όλες',
    'reminder.tabPending': 'Εκκρεμείς',
    'reminder.tabOverdue': 'Εκπρόθεσμες',
    'reminder.tabCompleted': 'Ολοκληρωμένες',

    'commission.title': 'Προμήθειες',
    'commission.status': 'Κατάσταση προμήθειας',
    'commission.subtitle': 'Εκκαθαρίσεις ασφαλιστικών εταιρειών',
    'commission.settlement': 'Εκκαθάριση',
    'commission.reference': 'Αρ. παραστατικού',
    'commission.statementDate': 'Ημ. εκκαθάρισης',
    'commission.declared': 'Δηλωθέν ποσό',
    'commission.expected': 'Αναμενόμενο',
    'commission.variance': 'Διαφορά',
    'commission.matched': 'Συμβόλαια',
    'commission.producerSplits': 'Απόδοση συνεργατών',
    'commission.producerSplitsHint': 'Μόνο εισπραγμένα συμβόλαια — γι\' αυτό τα νούμερα διαφέρουν από τις Αναφορές',
    'commission.share': 'Ποσοστό',
    'commission.producerShare': 'Μερίδιο συνεργάτη',

    'report.title': 'Αναφορές',
    'report.subtitle': 'Παραγωγή, κατανομή και απόδοση',
    'report.byCompany': 'Ανά ασφαλιστική',
    'report.byType': 'Ανά κλάδο',
    'report.byAgent': 'Ανά συνεργάτη',
    'report.byClient': 'Ανά πελάτη',
    'report.agentPerformance': 'Απόδοση συνεργατών',
    'report.claimsAgeing': 'Παλαιότητα ζημιών',
    'report.policyCount': 'Συμβόλαια',
    'report.claimsHandled': 'Ζημιές',
    'report.renewalsCompleted': 'Ανανεώσεις',

    'settings.title': 'Ρυθμίσεις',
    'settings.appearance': 'Εμφάνιση',
    'settings.theme': 'Θέμα',
    'settings.themeLight': 'Φωτεινό',
    'settings.themeDark': 'Σκοτεινό',
    'settings.themeSystem': 'Συστήματος',
    'settings.language': 'Γλώσσα',
    'settings.density': 'Πυκνότητα πινάκων',
    'settings.densityComfortable': 'Άνετη',
    'settings.densityCompact': 'Συμπυκνωμένη',

    'user.title': 'Χρήστες',
    'user.subtitle': 'Λογαριασμοί και δικαιώματα',
    'user.one': 'Χρήστης',
    'user.role': 'Ρόλος',
    'user.active': 'Ενεργός',
    'user.lastLogin': 'Τελευταία σύνδεση',
    'user.commissionShare': 'Ποσοστό προμήθειας',
    'user.resetPassword': 'Επαναφορά κωδικού',

    'company.title': 'Ασφαλιστικές εταιρείες',
    'company.subtitle': 'Στοιχεία συνεργαζόμενων εταιρειών',
    'company.one': 'Ασφαλιστική εταιρεία',
    'company.name': 'Επωνυμία',
    'company.code': 'Κωδικός',
    'company.defaultCommission': 'Προεπιλεγμένη προμήθεια',
    'commission.missingNet':
      '{count} συμβόλαια δεν έχουν καθαρά ασφάλιστρα — η προμήθειά τους μετράει 0 στα παραπάνω σύνολα.',
    'endorsement.netUnchanged':
      'Τα καθαρά μένουν ως έχουν — η προμήθεια δεν θα αλλάξει. Συμπλήρωσέ τα αν άλλαξαν κι αυτά.',
    'insurer.rates': 'Προμήθειες ανά κατηγορία οχήματος',
    'insurer.ratesHint':
      'Κάθε εταιρεία πληρώνει διαφορετικά ανά κατηγορία. Όσες αφήσεις κενές παίρνουν την προεπιλεγμένη προμήθεια. Τα υπάρχοντα συμβόλαια κρατούν το ποσοστό με το οποίο γράφτηκαν.',
    'insurer.ratesEmpty': 'Καμία κατηγορία — όλα τα συμβόλαια παίρνουν την προεπιλεγμένη προμήθεια.',
    'insurer.ratesFallback': 'προεπιλογή',
    'insurer.ratesSaved': 'Οι προμήθειες αποθηκεύτηκαν',
    'company.settlementDays': 'Ημέρες εκκαθάρισης',
    'company.policies': 'Συμβόλαια',

    'audit.title': 'Ιστορικό ενεργειών',
    'audit.subtitle': 'Πλήρες αρχείο μεταβολών',
    'audit.entity': 'Οντότητα',
    'audit.action': 'Ενέργεια',
    'audit.changes': 'Μεταβολές',
    'audit.user': 'Χρήστης',
    /* Audit log vocabulary. The log stores raw table and column names; these give them
       a human face without the server needing to know the UI's language. */
    'audit.entity.policy': 'Συμβόλαιο',
    'audit.entity.client': 'Πελάτης',
    'audit.entity.claim': 'Ζημιά',
    'audit.entity.user': 'Χρήστης',
    'audit.entity.auth': 'Σύνδεση',
    'audit.entity.reminder': 'Υπενθύμιση',
    'audit.entity.installment': 'Δόση',
    'audit.entity.commission_settlement': 'Εκκαθάριση',
    'audit.entity.insurance_company': 'Ασφαλιστική',
    'audit.entity.document': 'Έγγραφο',
    'audit.field.startDate': 'Έναρξη',
    'audit.field.noticeStatus': 'Ειδοποιητήριο',
    'audit.field.noticeNote': 'Σημείωση / λόγος μη ανανέωσης',
    'audit.field.endDate': 'Λήξη',
    'audit.field.premiumAmount': 'Ασφάλιστρο',
    'audit.field.netPremiumAmount': 'Καθαρά',
    'audit.field.commissionPercentage': 'Ποσοστό προμήθειας',
    'audit.field.commissionAmount': 'Προμήθεια',
    'audit.field.status': 'Κατάσταση',
    'audit.field.paymentStatus': 'Πληρωμή',
    'audit.field.commissionStatus': 'Κατάσταση προμήθειας',
    'audit.field.nextRenewalDate': 'Επόμενη ανανέωση',
    'audit.field.currentPeriodNumber': 'Περίοδος',
    'audit.field.fullName': 'Ονοματεπώνυμο',
    'audit.field.phone': 'Τηλέφωνο',
    'audit.field.email': 'Email',
    'audit.field.succeeded': 'Επιτυχή',
    'audit.field.failed': 'Αποτυχημένα',
    'audit.field.assignedAgentId': 'Υπεύθυνος συνεργάτης',
    'audit.noChanges': 'Χωρίς καταγεγραμμένες μεταβολές πεδίων',

    'import.title': 'Εισαγωγή δεδομένων',
    'import.subtitle': 'Μεταφορά πελατολογίου από αρχείο CSV',
    'import.dropzone': 'Σύρετε αρχείο CSV εδώ ή επιλέξτε',
    'import.template': 'Λήψη υποδείγματος',
    'import.dryRun': 'Δοκιμαστική εκτέλεση (χωρίς αποθήκευση)',
    'import.run': 'Εκτέλεση εισαγωγής',
    'import.totalRows': 'Γραμμές',
    'import.validRows': 'Έγκυρες',
    'import.imported': 'Εισήχθησαν',
    'import.skipped': 'Παραλείφθηκαν',
    'import.errors': 'Σφάλματα',
    'import.row': 'Γραμμή',

    'search.placeholder': 'Αναζήτηση πελάτη, συμβολαίου, ζημιάς…',
    'search.short': 'Αναζήτηση…',
    'search.commandHint': 'Πληκτρολογήστε για αναζήτηση ή επιλέξτε ενέργεια',
    'search.noResults': 'Κανένα αποτέλεσμα',
    'search.groupClients': 'Πελάτες',
    'search.groupPolicies': 'Συμβόλαια',
    'search.groupClaims': 'Ζημιές',
    'search.groupActions': 'Ενέργειες',
    'search.groupNavigation': 'Πλοήγηση',
    'search.hintNavigate': 'πλοήγηση',
    'search.hintOpen': 'άνοιγμα',

    /**
     * Section headings shared by every entity detail page. Kept in one namespace
     * precisely because they are shared — "Οικονομικά" must read identically on a
     * policy, a claim and an insurer, or the pages stop feeling like one product.
     */
    'section.identity': 'Ταυτότητα',
    'section.contact': 'Επικοινωνία',
    'section.term': 'Διάρκεια & ανανέωση',
    'section.financial': 'Οικονομικά',
    'section.coverage': 'Κάλυψη',
    'section.incident': 'Περιστατικό',
    'section.handling': 'Διαχείριση',
    'section.relationships': 'Σχέσεις',
    'section.performance': 'Απόδοση',
    'section.terms': 'Όροι συνεργασίας',
    'section.notes': 'Σημειώσεις',
    'section.cancellation': 'Ακύρωση',

    /* Header context strip — deliberately terse; the numbers carry the meaning. */
    'topbar.today': 'Σήμερα',
    'topbar.renewals': 'ανανεώσεις',
    'topbar.claims': 'ζημιές',
    'topbar.unpaid': 'ανεξόφλητα',
    'topbar.allClear': 'όλα τακτοποιημένα',

    'notification.title': 'Ειδοποιήσεις',
    'notification.empty': 'Καμία ειδοποίηση',
    'notification.markAllRead': 'Σήμανση όλων ως αναγνωσμένων',

    'today.subtitle': 'Ό,τι χρειάζεται ενέργεια σήμερα, σε μία ματιά',
    'today.mineOnly': 'Μόνο δικά μου',
    'today.print': 'Εκτύπωση',
    'today.renewals': 'Κλήσεις ανανέωσης',
    'today.renewalsHint': 'Λήγουν εντός 7 ημερών — και έως 3 ημέρες μετά τη λήξη',
    'today.collections': 'Εισπράξεις εβδομάδας',
    'today.collectionsHint': 'Δόσεις εκπρόθεσμες ή με λήξη έως 7 ημέρες',
    'today.overdueAmount': 'Εκπρόθεσμα',
    'today.dueSoonAmount': 'Έως 7 ημέρες',
    'today.reminders': 'Εκπρόθεσμες υπενθυμίσεις',
    'today.birthdays': 'Γενέθλια',
    'today.birthdaysHint': 'Επόμενες 14 ημέρες — μια καλή αφορμή επικοινωνίας',
    'today.turns': 'Κλείνει τα {n}',
    'today.expiringDocs': 'Έγγραφα που λήγουν',
    'today.expiringDocsHint': 'Έγγραφα πελατών με ημερομηνία λήξης εντός 60 ημερών',
    'today.lapsed': '{n} συμβόλαια έληξαν ανεξόφλητα το τελευταίο τρίμηνο — αξία {amount}',
    'today.lapsedAction': 'Λίστα επανάκτησης',
    'today.allClear': 'Όλα τακτοποιημένα',
    'today.call': 'Κλήση',

    'collections.title': 'Εισπράξεις',
    'collections.subtitle': 'Ανεξόφλητες δόσεις σε όλα τα συμβόλαια — οι πιο καθυστερημένες πρώτες',

    'notices.title': 'Ειδοποιητήρια',
    'notices.subtitle': 'Ποιους πρέπει να πάρεις τηλέφωνο για ανανέωση — οι πιο επείγοντες πρώτοι',
    'notices.status': 'Ειδοποίηση',
    'notices.received': 'Ειδοποιητήριο',
    'notices.notifiedAt': 'Ειδοποιήθηκε',
    'notices.note': 'Τι είπε ο πελάτης',
    'notices.record': 'Καταγραφή ειδοποίησης',
    'notices.markNotified': 'Ειδοποιήθηκε',
    'notices.pendingCount': 'Εκκρεμούν τηλεφωνήματα',
    'notices.showDeclined': 'Με «Δεν ανανεώνει»',
    'notices.empty': 'Κανένα εκκρεμές ειδοποιητήριο',
    'notices.hint':
      'Η εταιρεία εκδίδει ειδοποιητήριο πριν τη λήξη. Εδώ καταγράφεις το τηλεφώνημα στον πελάτη και τι απάντησε.',

    'notices.email': 'Email',
    'notices.emailSend': 'Αποστολή ειδοποιητηρίου',
    'notices.emailSendShort': 'Αποστολή',
    'notices.emailResend': 'Αποστολή ξανά',
    'notices.emailSent': 'Στάλθηκε',
    'notices.emailNotSent': 'Δεν έχει σταλεί',
    'notices.emailSentAt': 'Email στις {date}',
    'notices.emailRecipient': 'Παραλήπτης',
    'notices.emailSubject': 'Θέμα',
    'notices.emailPreview': 'Προεπισκόπηση',
    'notices.emailNoAddress': 'Ο πελάτης δεν έχει email',
    'notices.emailDisabled': 'Τα email ειδοποιητηρίων είναι απενεργοποιημένα στις Ρυθμίσεις',
    'notices.emailSendDue': 'Αποστολή στα εκκρεμή',
    'notices.emailSendDueHint':
      'Στέλνει το ειδοποιητήριο σε όλα τα συμβόλαια που λήγουν μέσα στο διάστημα των ρυθμίσεων και δεν έχουν ήδη λάβει email.',
    'notices.emailSendSelected': 'Αποστολή email',
    'notices.emailBatchResult': '{sent} στάλθηκαν · {skipped} παραλείφθηκαν',
    'notices.emailAutoNotice': 'Αυτόματη αποστολή {days} ημέρες πριν τη λήξη',

    'settings.renewalEmail': 'Ειδοποιητήρια με email',
    'settings.renewalEmailHint':
      'Το μήνυμα που λαμβάνει ο πελάτης πριν τη λήξη του συμβολαίου του.',
    'settings.renewalEmailEnabled': 'Ενεργό',
    'settings.renewalEmailEnabledHint':
      'Όσο είναι ανενεργό δεν φεύγει κανένα email, ούτε αυτόματα ούτε χειροκίνητα.',
    'settings.renewalEmailMode': 'Τρόπος αποστολής',
    'settings.renewalEmailModeAuto': 'Αυτόματα',
    'settings.renewalEmailModeManual': 'Χειροκίνητα',
    'settings.renewalEmailModeAutoHint':
      'Φεύγει μόνο του κάθε πρωί, στα συμβόλαια που λήγουν μέσα στο παρακάτω διάστημα.',
    'settings.renewalEmailModeManualHint':
      'Δεν φεύγει τίποτα μόνο του — στέλνεις εσύ από τα Ειδοποιητήρια, ανά πελάτη ή όλα μαζί.',
    'settings.renewalEmailLeadDays': 'Ημέρες πριν τη λήξη',
    'settings.renewalEmailLeadDaysAuto': 'Τόσες μέρες πριν λήξει το συμβόλαιο φεύγει το email.',
    'settings.renewalEmailLeadDaysManual':
      'Το ίδιο διάστημα ορίζει και τι στέλνει το κουμπί «Αποστολή στα εκκρεμή» στα Ειδοποιητήρια.',
    'settings.renewalEmailSubject': 'Θέμα',
    'settings.renewalEmailBody': 'Κείμενο',
    'settings.renewalEmailSender': 'Όνομα αποστολέα',
    'settings.renewalEmailReplyTo': 'Απάντηση σε (email)',
    'settings.renewalEmailPlaceholders': 'Πεδία που μπορείς να χρησιμοποιήσεις',
    'settings.renewalEmailPlaceholdersHint':
      'Κάνε κλικ σε ένα πεδίο για να μπει στο κείμενο. Αντικαθίστανται με τα στοιχεία του κάθε συμβολαίου.',
    'settings.renewalEmailTest': 'Δοκιμαστική αποστολή',
    'settings.renewalEmailTestHint': 'Στέλνει το πρότυπο με δείγμα στοιχείων σε μία διεύθυνση.',
    'settings.renewalEmailTestSent': 'Το δοκιμαστικό email στάλθηκε στο {email}',
    'settings.renewalEmailNoSmtp':
      'Δεν έχει ρυθμιστεί SMTP server — τα μηνύματα καταγράφονται στο log αντί να αποστέλλονται.',
    'settings.renewalEmailUpdatedBy': 'Τελευταία αλλαγή: {name}, {date}',
    'collections.installment': 'Δόση',
    'collections.outstanding': 'Σύνολο προς είσπραξη',

    'policy.cancelPreview': 'Υπολογισμός ακυρωτικού',
    'policy.periodUsed': 'Διανυθείσα περίοδος: {used} από {total} ημέρες',
    'policy.earnedPremium': 'Δεδουλευμένα ασφάλιστρα',
    'policy.refundDue': 'Επιστροφή στον πελάτη (ακυρωτικό)',
    'policy.refundNotPaid': 'Η περίοδος δεν έχει εξοφληθεί — δεν προκύπτει επιστροφή',
    'policy.commissionClawback': 'Αναλογική επιστροφή προμήθειας στην εταιρεία',
    'policy.refund': 'Ακυρωτικό',
    'policy.bmClass': 'Bonus-Malus',

    'client.opportunities': 'Ευκαιρίες πώλησης',
    'client.opportunitiesHint': 'Βασικοί κλάδοι χωρίς ενεργό συμβόλαιο',
    'client.since': 'Πελάτης από',

    'offline.online': 'Σε σύνδεση',
    'offline.offline': 'Εκτός σύνδεσης',
    'offline.onlineHint': 'Όλες οι ενέργειες είναι διαθέσιμες.',
    'offline.offlineHint': 'Μπορείτε να δείτε δεδομένα και να καταγράψετε σημειώσεις.',
    'offline.readOnlyHint': 'Τα δεδομένα προβάλλονται από την τοπική μνήμη.',
    'offline.allSynced': 'Όλα συγχρονισμένα',
    'offline.syncNow': 'Συγχρονισμός',
    'offline.queued': 'Καταχωρήθηκε τοπικά',
    'offline.pending': 'Εκκρεμεί',
    'offline.pendingHint': 'Θα σταλεί μόλις επανέλθει η σύνδεση.',
    'offline.attempts': 'προσπάθειες: {n}',
    'offline.bannerTitle': 'Εργάζεστε εκτός σύνδεσης',
    'offline.bannerHint': 'Οι υπενθυμίσεις και οι σημειώσεις αποθηκεύονται τοπικά.',
    'offline.bannerQueued': '{n} ενέργειες σε αναμονή αποστολής',
    'offline.bannerQueuedOne': '1 ενέργεια σε αναμονή αποστολής',
    'offline.requiresConnection': 'Απαιτείται σύνδεση',
    'offline.policyHint': 'Εκτός σύνδεσης επιτρέπονται μόνο υπενθυμίσεις και σημειώσεις. Πληρωμές, συμβόλαια και στοιχεία πελατών απαιτούν σύνδεση.',
    'offline.op.reminderCreate': 'Νέα υπενθύμιση',
    'offline.op.reminderComplete': 'Ολοκλήρωση υπενθύμισης',
    'offline.op.reminderReopen': 'Επαναφορά υπενθύμισης',
    'offline.op.activityCreate': 'Καταγραφή επικοινωνίας',
    'offline.op.timelineCreate': 'Σημείωση σε ζημιά',
    'offline.blocked.financial': 'αφορά χρηματικές κινήσεις',
    'offline.blocked.contract': 'μεταβάλλει συμβόλαιο ή ζημιά',
    'offline.blocked.personalData': 'αφορά προσωπικά δεδομένα',
    'offline.blocked.security': 'αφορά ασφάλεια συστήματος',

    'error.notFound': 'Η σελίδα δεν βρέθηκε',
    'error.notFoundHint': 'Ο σύνδεσμος ίσως έχει αλλάξει ή η εγγραφή διαγράφηκε.',
    'error.forbidden': 'Δεν έχετε πρόσβαση',
    'error.forbiddenHint': 'Ο ρόλος σας δεν επιτρέπει την προβολή αυτής της σελίδας.',
    'error.generic': 'Κάτι πήγε στραβά',

    /* enum labels — resolved generically, never inlined in a screen */
    'enum.role.admin': 'Διαχειριστής',
    'enum.role.agent': 'Συνεργάτης',
    'enum.role.viewer': 'Λογιστήριο',

    'enum.clientType.individual': 'Φυσικό πρόσωπο',
    'enum.clientType.company': 'Εταιρεία',

    'enum.insuranceType.auto': 'Αυτοκίνητο',
    'enum.insuranceType.roadside_assistance': 'Οδική βοήθεια',
    'enum.insuranceType.health': 'Υγεία',
    'enum.insuranceType.property': 'Περιουσία',
    'enum.insuranceType.life': 'Ζωή',
    'enum.insuranceType.travel': 'Ταξιδιωτική',
    'enum.insuranceType.liability': 'Αστική ευθύνη',
    'enum.insuranceType.marine': 'Σκάφη',
    'enum.insuranceType.professional_indemnity': 'Επαγγελματική ευθύνη',
    'enum.insuranceType.cyber': 'Κυβερνοασφάλιση',
    'enum.insuranceType.other': 'Λοιπά',

    'enum.insurerDocumentType.terms': 'Όροι',
    'enum.insurerDocumentType.contract': 'Σύμβαση',
    'enum.insurerDocumentType.commission_agreement': 'Συμφωνία προμηθειών',
    'enum.insurerDocumentType.product_sheet': 'Φύλλο προϊόντος',
    'enum.insurerDocumentType.price_list': 'Τιμοκατάλογος',
    'enum.insurerDocumentType.circular': 'Εγκύκλιος',
    'enum.insurerDocumentType.other': 'Άλλο',

    'insurer.claimsPhone': 'Τηλέφωνο ατυχήματος',
    'insurer.roadsidePhone': 'Οδική βοήθεια',
    'insurer.emergencyPhone': '24ωρη εξυπηρέτηση',
    'insurer.portalUrl': 'Portal εταιρείας',
    'insurer.clientFacing': 'Τηλέφωνα για τον πελάτη',
    'insurer.clientFacingHint':
      'Αυτά τα δίνεις στον πελάτη — δεν είναι το τηλέφωνο του δικού σου συνεργάτη στην εταιρεία.',
    'insurer.documents': 'Όροι & συμβάσεις',

    'enum.renewalNoticeStatus.pending': 'Εκκρεμεί',
    'enum.renewalNoticeStatus.notified': 'Ειδοποιήθηκε',
    'enum.renewalNoticeStatus.confirmed': 'Θα ανανεώσει',
    'enum.renewalNoticeStatus.declined': 'Δεν ανανεώνει',

    'enum.frequency.monthly': 'Μηνιαία',
    'enum.frequency.quarterly': 'Τριμηνιαία',
    'enum.frequency.semiannual': 'Εξαμηνιαία',
    'enum.frequency.annual': 'Ετήσια',
    'enum.frequency.one_off': 'Εφάπαξ',

    'enum.policyStatus.active': 'Ενεργό',
    'enum.policyStatus.pending': 'Σε εκκρεμότητα',
    'enum.policyStatus.expired': 'Ληγμένο',
    'enum.policyStatus.cancelled': 'Ακυρωμένο',
    'enum.policyStatus.renewal_due': 'Προς ανανέωση',
    'enum.policyStatus.paid': 'Ολοκληρωμένο (εφάπαξ)',

    'enum.paymentStatus.unpaid': 'Ανεξόφλητο',
    'enum.paymentStatus.partially_paid': 'Μερικώς εξοφλημένο',
    'enum.paymentStatus.paid': 'Εξοφλημένο',

    'enum.claimStatus.open': 'Ανοιχτή',
    'enum.claimStatus.in_progress': 'Σε εξέλιξη',
    'enum.claimStatus.waiting_documents': 'Αναμονή εγγράφων',
    'enum.claimStatus.approved': 'Εγκρίθηκε',
    'enum.claimStatus.rejected': 'Απορρίφθηκε',
    'enum.claimStatus.paid': 'Αποζημιώθηκε',
    'enum.claimStatus.closed': 'Κλειστή',

    'enum.reminderStatus.pending': 'Εκκρεμεί',
    'enum.reminderStatus.completed': 'Ολοκληρώθηκε',
    'enum.reminderStatus.overdue': 'Εκπρόθεσμη',

    'enum.priority.low': 'Χαμηλή',
    'enum.priority.normal': 'Κανονική',
    'enum.priority.high': 'Υψηλή',

    'enum.installmentStatus.pending': 'Εκκρεμεί',
    'enum.installmentStatus.paid': 'Πληρώθηκε',
    'enum.installmentStatus.overdue': 'Εκπρόθεσμη',
    'enum.installmentStatus.cancelled': 'Ακυρώθηκε',

    'enum.paymentMethod.cash': 'Μετρητά',
    'enum.paymentMethod.card': 'Κάρτα',
    'enum.paymentMethod.bank_transfer': 'Τραπεζικό έμβασμα',
    'enum.paymentMethod.direct_debit': 'Πάγια εντολή',
    'enum.paymentMethod.cheque': 'Επιταγή',

    'enum.commissionStatus.expected': 'Αναμενόμενη',
    'enum.commissionStatus.statement_received': 'Σε εκκαθάριση',
    'enum.commissionStatus.settled': 'Εξοφλημένη',
    'enum.commissionStatus.disputed': 'Σε διαφωνία',

    'enum.settlementStatus.draft': 'Πρόχειρη',
    'enum.settlementStatus.reconciling': 'Σε συμφωνία',
    'enum.settlementStatus.closed': 'Κλεισμένη',

    'enum.endorsementType.coverage_change': 'Μεταβολή κάλυψης',
    'enum.endorsementType.premium_adjustment': 'Αναπροσαρμογή ασφαλίστρου',
    'enum.endorsementType.vehicle_change': 'Αλλαγή οχήματος',
    'enum.endorsementType.driver_change': 'Αλλαγή οδηγού',
    'enum.endorsementType.address_change': 'Αλλαγή διεύθυνσης',
    'enum.endorsementType.frequency_change': 'Αλλαγή συχνότητας',
    'enum.endorsementType.suspension': 'Αναστολή',
    'enum.endorsementType.reinstatement': 'Επαναφορά',
    'enum.endorsementType.other': 'Λοιπά',

    'enum.insuredItemType.vehicle': 'Όχημα',
    'enum.insuredItemType.property': 'Ακίνητο',
    'enum.insuredItemType.person': 'Πρόσωπο',
    'enum.insuredItemType.vessel': 'Σκάφος',
    'enum.insuredItemType.other': 'Άλλο',

    'enum.vehicleCategory.passenger_private': 'ΙΧ επιβατικό',
    'enum.vehicleCategory.passenger_rental': 'ΕΙΧ ενοικιαζόμενο',
    'enum.vehicleCategory.taxi': 'ΤΑΞΙ (ΕΔΧ)',
    'enum.vehicleCategory.truck_private': 'ΦΙΧ φορτηγό',
    'enum.vehicleCategory.truck_public': 'ΦΔΧ φορτηγό',
    'enum.vehicleCategory.agricultural': 'Αγροτικό',
    'enum.vehicleCategory.bus': 'Λεωφορείο',
    'enum.vehicleCategory.motorcycle': 'ΜΟΤΟ',
    'enum.vehicleCategory.motorcycle_rental': 'ΜΟΤΟ ενοικιαζόμενο',
    'enum.vehicleCategory.trailer': 'Ρυμουλκούμενο',
    'enum.vehicleCategory.tractor': 'Τρακτέρ',
    'enum.vehicleCategory.other': 'Άλλο',

    'enum.clientDocumentType.id_card': 'Ταυτότητα',
    'enum.clientDocumentType.passport': 'Διαβατήριο',
    'enum.clientDocumentType.driving_license': 'Δίπλωμα οδήγησης',
    'enum.clientDocumentType.vehicle_reg': 'Άδεια κυκλοφορίας',
    'enum.clientDocumentType.company_doc': 'Εταιρικό έγγραφο',
    'enum.clientDocumentType.contract': 'Σύμβαση',
    'enum.clientDocumentType.other': 'Άλλο',

    'enum.policyDocumentType.policy_contract': 'Ασφαλιστήριο συμβόλαιο',
    'enum.policyDocumentType.payment_notice': 'Ειδοποιητήριο πληρωμής',
    'enum.policyDocumentType.endorsement': 'Πρόσθετη πράξη',
    'enum.policyDocumentType.green_card': 'Πράσινη κάρτα',
    'enum.policyDocumentType.proposal': 'Πρόταση ασφάλισης',
    'enum.policyDocumentType.cancellation': 'Ακυρωτικό',
    'enum.policyDocumentType.other': 'Άλλο',

    'enum.timelineAction.created': 'Δημιουργία',
    'enum.timelineAction.status_changed': 'Αλλαγή κατάστασης',
    'enum.timelineAction.document_uploaded': 'Μεταφόρτωση εγγράφου',
    'enum.timelineAction.note_added': 'Σημείωση',
    'enum.timelineAction.contact_insurer': 'Επικοινωνία με εταιρεία',
    'enum.timelineAction.payout_received': 'Λήψη αποζημίωσης',
    'enum.timelineAction.follow_up': 'Follow-up',

    'enum.activityType.call': 'Τηλεφώνημα',
    'enum.activityType.email': 'Email',
    'enum.activityType.meeting': 'Συνάντηση',
    'enum.activityType.note': 'Σημείωση',
    'enum.activityType.sms': 'SMS',
    'enum.activityType.visit': 'Επίσκεψη',

    'enum.direction.inbound': 'Εισερχόμενη',
    'enum.direction.outbound': 'Εξερχόμενη',

    'enum.auditAction.create': 'Δημιουργία',
    'enum.auditAction.update': 'Ενημέρωση',
    'enum.auditAction.delete': 'Διαγραφή',
    'enum.auditAction.login': 'Σύνδεση',
    'enum.auditAction.export': 'Εξαγωγή',
    'enum.auditAction.import': 'Εισαγωγή',
  },

  en: {
    'app.name': 'Orion',
    'app.shortName': 'Orion',
    'app.tagline': 'Insurance OS',

    'nav.today': 'My day',
    'nav.dashboard': 'Overview',
    'nav.collections': 'Collections',
    'nav.notices': 'Renewal notices',
    'nav.clients': 'Clients',
    'nav.policies': 'Policies',
    'nav.renewals': 'Renewals',
    'nav.claims': 'Claims',
    'nav.reminders': 'Reminders',
    'nav.commissions': 'Commissions',
    'nav.reports': 'Reports',
    'nav.settings': 'Settings',
    'nav.users': 'Users',
    'nav.companies': 'Insurers',
    'nav.audit': 'Audit log',
    'nav.import': 'Data import',
    'nav.groupMain': 'Daily',
    'nav.groupInsight': 'Insight',
    'nav.groupAdmin': 'Administration',
    'nav.collapse': 'Collapse',

    'action.create': 'Create',
    'action.edit': 'Edit',
    'action.save': 'Save',
    'action.cancel': 'Cancel',
    'action.delete': 'Delete',
    'action.close': 'Close',
    'action.confirm': 'Confirm',
    'action.search': 'Search',
    'action.filter': 'Filters',
    'action.clear': 'Clear',
    'action.clearAll': 'Clear all',
    'action.export': 'Export',
    'action.import': 'Import',
    'action.view': 'View',
    'action.back': 'Back',
    'action.add': 'Add',
    'action.upload': 'Upload',
    'action.download': 'Download',
    'action.retry': 'Retry',
    'action.markPaid': 'Mark as paid',
    'action.renewPeriod': 'Renew',
    'action.renew': 'Renew',
    'action.markComplete': 'Complete',
    'action.reopen': 'Reopen',
    'action.assign': 'Assign',
    'action.more': 'More',
    'action.selectAll': 'Select all',
    'action.copy': 'Copy',
    'action.newPolicy': 'New policy',
    'action.newClaim': 'New claim',
    'action.newReminder': 'New reminder',
    'action.newClient': 'New client',

    'common.loading': 'Loading…',
    'common.noResults': 'No results found',
    'common.noResultsHint': 'Try changing or clearing the filters.',
    'common.empty': 'Nothing here yet',
    'common.error': 'Something went wrong',
    'common.all': 'All',
    'common.none': 'None',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.total': 'Total',
    'common.of': 'of',
    'common.rows': 'rows',
    'common.rowsPerPage': 'Per page',
    'common.page': 'Page',
    'common.selected': 'selected',
    'common.required': 'Required',
    'error.invalidInput': 'Invalid value',
    'error.invalidNumber': 'Expected a number',
    'common.optional': 'optional',
    'common.unassigned': 'Unassigned',
    'common.never': 'Never',
    'common.today': 'Today',
    'common.days': 'days',
    'common.daysShort': 'd',
    'common.daysLeft': 'days left',
    'common.daysOverdue': 'days overdue',
    'common.expired': 'Expired',
    'common.from': 'From',
    'common.to': 'To',
    'common.dateRange': 'Date range',
    'common.density': 'Density',
    'common.columns': 'Columns',
    'common.actions': 'Actions',
    'common.details': 'Details',
    'common.history': 'History',
    'common.documents': 'Documents',
    'common.notes': 'Notes',
    'common.createdAt': 'Created',
    'common.updatedAt': 'Updated',
    'common.createdBy': 'By',
    'common.saved': 'Saved',
    'common.deleted': 'Deleted',
    'count.selected.one': '{n} selected',
    'count.selected.other': '{n} selected',
    'count.rows.one': '{n} row',
    'count.rows.other': '{n} rows',
    'count.updated.one': '{n} row updated',
    'count.updated.other': '{n} rows updated',
    'table.footerBasis.one': 'Total across {n} filtered row',
    'table.footerBasis.other': 'Total across all {n} filtered rows',
    'count.policies.one': '{n} policy',
    'count.policies.other': '{n} policies',

    'auth.signIn': 'Sign in',
    'auth.signOut': 'Sign out',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.rememberMe': 'Remember me',
    'auth.welcome': 'Welcome back',
    'auth.subtitle': 'Sign in to continue.',
    'auth.brandLine': 'Built for Insurance.',
    'auth.demoHint': 'Demo accounts',
    'auth.changePassword': 'Change password',
    'auth.currentPassword': 'Current password',
    'auth.newPassword': 'New password',
    'auth.profile': 'My account',
    'auth.sessionExpired': 'Your session expired. Please sign in again.',
    'dialog.newClientHint': 'Contact and tax details — the rest can follow later',
    'dialog.editClientHint': 'Change this client’s details',
    'dialog.newPolicyHint': 'Cover, term and premium — instalments are derived automatically',
    'dialog.newClaimHint': 'Log the incident; the carrier’s reference can be added later',
    'dialog.editClaimHint': 'Change this claim’s details',
    'auth.changePasswordHint': 'At least 10 characters, with lower case, upper case and a digit',
    'auth.forgotPassword': 'Forgot your password?',
    'auth.forgotPasswordHint': 'Enter the email of your account and we will send you a link to set a new password.',
    'auth.sendResetLink': 'Send link',
    'auth.resetLinkSent': 'If an account exists for this email, a reset link has been sent. It is valid for 60 minutes — check your spam folder too.',
    'auth.backToSignIn': 'Back to sign in',
    'auth.resetPasswordTitle': 'New password',
    'auth.resetPasswordHint': 'Set a new password for your account. Every signed-in device will be signed out.',
    'auth.confirmPassword': 'Confirm password',
    'auth.passwordsDoNotMatch': 'The passwords do not match',
    'auth.resetDone': 'Your password has been changed. Sign in with the new one.',
    'auth.resetLinkMissing': 'The link is incomplete. Open it again from the email, or request a new one.',
    'auth.requestNewLink': 'Request a new link',
    'document.dropzone': 'Drop a file here or browse',
    'confirm.cannotUndo': 'This cannot be undone.',
    'confirm.deleteDocument': 'Delete this document?',
    'confirm.discardChange': 'Discard this change?',
    'confirm.discardChangeHint':
      'This change was recorded offline and has not been sent yet. It will be lost for good.',
    'document.allTypes': 'All types',
    'document.filterByType': 'Filter by type',
    'document.preview': 'Preview',
    'document.previewUnsupported': 'This file type cannot be previewed',
    'document.previewNeedsConnection': 'The preview fetches the file to display it, so it needs a connection',

    'dashboard.title': 'Overview',
    'dashboard.subtitle':
      'Premium and commission cover the selected period — every other figure reports the book as it stands now',
    'dashboard.period': 'Period',
    'dashboard.period.30d': 'Last 30 days',
    'dashboard.period.90d': 'Last 90 days',
    'dashboard.period.365d': 'Last 12 months',
    'dashboard.period.all': 'All time',
    'dashboard.period.custom': 'Custom',
    'dashboard.writtenFrom': 'Policy start from',
    'dashboard.writtenTo': 'Policy start to',
    'dashboard.totalPremium': 'Premium',
    'dashboard.totalCommission': 'Commission',
    'dashboard.activePolicies': 'Active policies',
    'dashboard.upcomingRenewals': 'Upcoming renewals',
    'dashboard.unpaidPolicies': 'Unpaid',
    'dashboard.unpaidPoliciesHint': 'Policies still owing — cancelled and expired excluded',
    'dashboard.receivablesBasis': 'across instalments',
    'dashboard.openClaims': 'Open claims',
    'dashboard.averagePremium': 'Average premium',
    'dashboard.receivables': 'Receivables',
    'dashboard.unreconciled': 'Commission awaiting settlement',
    'dashboard.unreconciledHint': 'Collected policies not yet included in a carrier statement',
    'dashboard.retention': 'Retention rate',
    'dashboard.premiumByMonth': 'Premium & commission by month',
    'dashboard.premiumByMonthHint': 'Agency commission as a share of total premium',
    'dashboard.byType': 'Distribution by product',
    'dashboard.byTypeHint': 'Active policies, by premium volume',
    'dashboard.upcomingList': 'Renewals in the next 30 days',
    'dashboard.overdueList': 'Overdue reminders',
    'dashboard.netPremium': 'Net premium',
    'dashboard.commission': 'Agency commission',
    'dashboard.vsPrevious': 'vs previous period',
    'dashboard.showTable': 'Show table',
    'dashboard.showChart': 'Show chart',

    'client.title': 'Clients',
    'client.subtitle': 'The agency book of clients',
    'client.one': 'Client',
    'client.fullName': 'Name / Company',
    'client.type': 'Type',
    'client.taxNumber': 'TIN',
    'client.idNumber': 'ID number',
    'client.drivingLicense': 'Driving licence',
    'client.dateOfBirth': 'Date of birth',
    'client.contactPerson': 'Contact person',
    'client.phone': 'Phone',
    'client.email': 'Email',
    'client.address': 'Address',
    'client.city': 'City',
    'client.postalCode': 'Postcode',
    'client.iban': 'IBAN',
    'client.tags': 'Tags',
    'client.agent': 'Account agent',
    'client.marketingConsent': 'Marketing consent',
    'client.activePolicies': 'Active policies',
    'client.vehicles': 'Vehicles',
    'client.vehiclesHint': '{active} on an active policy',
    'client.vehiclesNoneActive': 'none active',
    'vehicle.insured': 'Insured',
    'vehicle.notInsured': 'Not active',
    'client.searchPlateHint': 'Search by plate number too',
    'client.activities': 'Communication',
    'client.exportData': 'Export data (GDPR)',
    'client.newActivity': 'Log communication',

    'policy.title': 'Policies',
    'policy.subtitle': 'Every policy, most recently entered first',
    'policy.one': 'Policy',
    'policy.number': 'Policy no.',
    'policy.renewalNumber': 'Renewal cert. no.',
    'policy.renewalNumberHint': 'Optional — some insurers keep the same central number',
    'policy.nextRenewalNumber': 'New renewal cert. no.',
    'policy.nextRenewalNumberHint':
      'The number on the certificate issued for the new period. Leave blank if the insurer keeps the same central number.',
    'policy.company': 'Insurer',
    'policy.insuranceType': 'Product',
    'policy.startDate': 'Start',
    'policy.endDate': 'End',
    'policy.frequency': 'Frequency',
    'policy.premium': 'Gross premium',
    'policy.premiumHint': 'What the client pays — taxes and levies included',
    'policy.netPremium': 'Net premium',
    'policy.netPremiumHint': 'The base the commission is calculated on',
    'policy.netPremiumMissing': 'Net premium missing',
    'policy.netPremiumMissingHint':
      'Without a net premium the commission stays 0 — it is never computed on the gross.',
    'policy.commission': 'Commission',
    'policy.commissionPct': 'Commission rate',
    'policy.specialCommission': 'Negotiated rate',
    'policy.amountIncomplete': 'Finish typing the amount before confirming',
    'policy.previewStale': 'Figures were not recalculated — check the amount you typed',
    'policy.vehicleCategory': 'Vehicle category',
    'policy.vehicleCategoryHint': "Decides the carrier's commission rate",
    'policy.status': 'Status',
    'policy.paymentStatus': 'Payment',
    'policy.coverage': 'Coverage',
    'policy.daysUntilExpiry': 'Days to expiry',
    'policy.nextRenewal': 'Next renewal',
    'policy.renewalHistory': 'Renewal history',
    'policy.period': 'Period',
    'policy.installments': 'Instalments',
    'policy.installment': 'Instalment',
    'policy.endorsements': 'Endorsements',
    'endorsement.new': 'New endorsement',
    'endorsement.type': 'Type',
    'endorsement.number': 'Endorsement number',
    'endorsement.effectiveDate': 'Effective from',
    'endorsement.description': 'What changes',
    'endorsement.descriptionHint': 'What this alters on the policy — the next colleague reads this',
    'endorsement.newPremium': 'New premium',
    'endorsement.newPremiumHint': 'Leave blank if the premium is unchanged',
    'endorsement.newCommission': 'New commission rate',
    'endorsement.preview': 'Effect on the policy',
    'endorsement.currentPremium': 'Current premium',
    'endorsement.delta': 'Difference',
    'endorsement.noChange': 'Premium unchanged',
    'endorsement.installmentsNote': 'Unpaid instalments will be rebuilt against the new premium.',
    'endorsement.saved': 'Endorsement recorded',
    'endorsement.blockedCancelled': 'A cancelled policy cannot be endorsed',
    'policy.insuredItems': 'Insured items',
    'policy.plate': 'Plate no.',
    'policy.showNonRenewing': 'Non-renewing',
    'policy.markPaidTitle': 'Mark as paid & renew',
    'policy.renewTitle': 'Renew policy',
    'policy.renewOnlyTitle': 'Renew period',
    'policy.alreadyCollected':
      'This period is already collected, so nothing is charged. This archives it and moves the policy on to its next period. Record when it was collected so the history stays accurate.',
    'policy.renewalPreview': 'Renewal preview',
    'policy.currentPeriod': 'Current period (will be archived)',
    'policy.nextPeriod': 'New period',
    'policy.willNotRenew': 'Will not renew',
    'policy.cancelTitle': 'Cancel policy',
    'action.cancelPolicy': 'Cancel',
    'policy.deleteTitle': 'Delete policy?',
    'policy.deleteHint': 'For entries made in error, e.g. a duplicate. The policy disappears everywhere with no refund computed; the deletion stays in the history. If the client stopped the cover, use «Cancel».',
    'policy.deleted': 'Policy deleted',
    'policy.cancelReason': 'Cancellation reason',
    'policy.newEndorsement': 'New endorsement',
    'policy.premiumDelta': 'Premium change',
    'policy.effectiveDate': 'Effective date',
    'policy.dueDate': 'Due date',
    'policy.amount': 'Amount',
    'policy.paymentMethod': 'Payment method',
    'policy.recordPayment': 'Record payment',
    'policy.bulkAction': 'Bulk action',

    'renewals.title': 'Renewals',
    'renewals.subtitle': 'Renewal workload by urgency',
    'renewals.critical': 'Critical (< 7 days)',
    'renewals.soon': 'Soon (7–14 days)',
    'renewals.upcoming': 'Upcoming (15–30 days)',
    'renewals.lapsed': 'Lapsed',
    'renewals.empty': 'Nothing in this band',
    'renewals.decline': 'Will not renew',
    'renewals.declineReason': 'Reason for not renewing',
    'renewals.declineReasonPlaceholder': 'e.g. sold the vehicle, moved to another agency',
    'renewals.declineTitle': 'Client will not renew?',
    'renewals.declineHint':
      'The policy leaves the Renewals board. It can be restored from Notices with the «Include not renewing» filter.',

    'claim.title': 'Claims',
    'claim.subtitle': 'Handling and tracking of claims',
    'claim.one': 'Claim',
    'claim.number': 'Claim no.',
    'claim.incidentDate': 'Incident date',
    'claim.reportDate': 'Reported',
    'claim.amount': 'Claimed',
    'claim.payout': 'Payout',
    'claim.daysOpen': 'Days open',
    'claim.timeline': 'Timeline',
    'claim.addEntry': 'Add entry',
    'claim.description': 'Incident description',
    'claim.changeStatus': 'Change status',
    'claim.systemEntry': 'system',
    'claim.statusChangedTo': 'Status changed from “{from}” to “{to}”',

    'reminder.title': 'Reminders',
    'reminder.subtitle': 'Outstanding follow-ups',
    'reminder.one': 'Reminder',
    'reminder.date': 'Date',
    'reminder.priority': 'Priority',
    'reminder.assignedTo': 'Assigned to',
    'reminder.relatedTo': 'Related to',
    'reminder.tabAll': 'All',
    'reminder.tabPending': 'Pending',
    'reminder.tabOverdue': 'Overdue',
    'reminder.tabCompleted': 'Completed',

    'commission.title': 'Commissions',
    'commission.status': 'Commission status',
    'commission.subtitle': 'Carrier commission statements',
    'commission.settlement': 'Statement',
    'commission.reference': 'Reference',
    'commission.statementDate': 'Statement date',
    'commission.declared': 'Declared',
    'commission.expected': 'Expected',
    'commission.variance': 'Variance',
    'commission.matched': 'Policies',
    'commission.producerSplits': 'Producer splits',
    'commission.producerSplitsHint': 'Collected policies only — which is why these differ from Reports',
    'commission.share': 'Share',
    'commission.producerShare': 'Producer amount',

    'report.title': 'Reports',
    'report.subtitle': 'Production, distribution and performance',
    'report.byCompany': 'By insurer',
    'report.byType': 'By product',
    'report.byAgent': 'By agent',
    'report.byClient': 'By client',
    'report.agentPerformance': 'Agent performance',
    'report.claimsAgeing': 'Claims ageing',
    'report.policyCount': 'Policies',
    'report.claimsHandled': 'Claims',
    'report.renewalsCompleted': 'Renewals',

    'settings.title': 'Settings',
    'settings.appearance': 'Appearance',
    'settings.theme': 'Theme',
    'settings.themeLight': 'Light',
    'settings.themeDark': 'Dark',
    'settings.themeSystem': 'System',
    'settings.language': 'Language',
    'settings.density': 'Table density',
    'settings.densityComfortable': 'Comfortable',
    'settings.densityCompact': 'Compact',

    'user.title': 'Users',
    'user.subtitle': 'Accounts and permissions',
    'user.one': 'User',
    'user.role': 'Role',
    'user.active': 'Active',
    'user.lastLogin': 'Last sign-in',
    'user.commissionShare': 'Commission share',
    'user.resetPassword': 'Reset password',

    'company.title': 'Insurance companies',
    'company.subtitle': 'Carrier reference data',
    'company.one': 'Insurance company',
    'company.name': 'Name',
    'company.code': 'Code',
    'company.defaultCommission': 'Default commission',
    'commission.missingNet':
      '{count} policies have no net premium — their commission counts as 0 in the totals above.',
    'endorsement.netUnchanged':
      'The net premium is unchanged, so the commission will not move. Fill it in if it changed too.',
    'insurer.rates': 'Commission by vehicle category',
    'insurer.ratesHint':
      'Every carrier pays a different rate per category. Categories left blank fall back to the default. Existing policies keep the rate they were written with.',
    'insurer.ratesEmpty': 'No categories set — every policy falls back to the default rate.',
    'insurer.ratesFallback': 'default',
    'insurer.ratesSaved': 'Commission rates saved',
    'company.settlementDays': 'Settlement days',
    'company.policies': 'Policies',

    'audit.title': 'Audit log',
    'audit.subtitle': 'Complete record of changes',
    'audit.entity': 'Entity',
    'audit.action': 'Action',
    'audit.changes': 'Changes',
    'audit.user': 'User',
    'audit.entity.policy': 'Policy',
    'audit.entity.client': 'Client',
    'audit.entity.claim': 'Claim',
    'audit.entity.user': 'User',
    'audit.entity.auth': 'Sign-in',
    'audit.entity.reminder': 'Reminder',
    'audit.entity.installment': 'Instalment',
    'audit.entity.commission_settlement': 'Settlement',
    'audit.entity.insurance_company': 'Carrier',
    'audit.entity.document': 'Document',
    'audit.field.startDate': 'Start date',
    'audit.field.noticeStatus': 'Renewal notice',
    'audit.field.noticeNote': 'Note / reason for not renewing',
    'audit.field.endDate': 'End date',
    'audit.field.premiumAmount': 'Premium',
    'audit.field.netPremiumAmount': 'Net premium',
    'audit.field.commissionPercentage': 'Commission rate',
    'audit.field.commissionAmount': 'Commission',
    'audit.field.status': 'Status',
    'audit.field.paymentStatus': 'Payment',
    'audit.field.commissionStatus': 'Commission status',
    'audit.field.nextRenewalDate': 'Next renewal',
    'audit.field.currentPeriodNumber': 'Period',
    'audit.field.fullName': 'Full name',
    'audit.field.phone': 'Phone',
    'audit.field.email': 'Email',
    'audit.field.succeeded': 'Succeeded',
    'audit.field.failed': 'Failed',
    'audit.field.assignedAgentId': 'Assigned agent',
    'audit.noChanges': 'No field-level changes recorded',

    'import.title': 'Data import',
    'import.subtitle': 'Migrate your client book from a CSV file',
    'import.dropzone': 'Drop a CSV file here, or browse',
    'import.template': 'Download template',
    'import.dryRun': 'Dry run (nothing is saved)',
    'import.run': 'Run import',
    'import.totalRows': 'Rows',
    'import.validRows': 'Valid',
    'import.imported': 'Imported',
    'import.skipped': 'Skipped',
    'import.errors': 'Errors',
    'import.row': 'Row',

    'search.placeholder': 'Search clients, policies, claims…',
    'search.short': 'Search anything…',
    'search.commandHint': 'Type to search, or pick an action',
    'search.noResults': 'No results',
    'search.groupClients': 'Clients',
    'search.groupPolicies': 'Policies',
    'search.groupClaims': 'Claims',
    'search.groupActions': 'Actions',
    'search.groupNavigation': 'Navigation',
    'search.hintNavigate': 'navigate',
    'search.hintOpen': 'open',

    'section.identity': 'Identity',
    'section.contact': 'Contact',
    'section.term': 'Term & renewal',
    'section.financial': 'Financial',
    'section.coverage': 'Coverage',
    'section.incident': 'Incident',
    'section.handling': 'Handling',
    'section.relationships': 'Relationships',
    'section.performance': 'Performance',
    'section.terms': 'Partnership terms',
    'section.notes': 'Notes',
    'section.cancellation': 'Cancellation',

    'topbar.today': 'Today',
    'topbar.renewals': 'renewals',
    'topbar.claims': 'claims',
    'topbar.unpaid': 'unpaid',
    'topbar.allClear': 'all clear',

    'notification.title': 'Notifications',
    'notification.empty': 'Nothing to report',
    'notification.markAllRead': 'Mark all as read',

    'today.subtitle': 'Everything that needs action today, at a glance',
    'today.mineOnly': 'Only mine',
    'today.print': 'Print',
    'today.renewals': 'Renewal calls',
    'today.renewalsHint': 'Expiring within 7 days — and up to 3 days past',
    'today.collections': "This week's collections",
    'today.collectionsHint': 'Instalments overdue or due within 7 days',
    'today.overdueAmount': 'Overdue',
    'today.dueSoonAmount': 'Next 7 days',
    'today.reminders': 'Overdue reminders',
    'today.birthdays': 'Birthdays',
    'today.birthdaysHint': 'Next 14 days — a good reason to call',
    'today.turns': 'Turns {n}',
    'today.expiringDocs': 'Documents expiring',
    'today.expiringDocsHint': 'Client documents expiring within 60 days',
    'today.lapsed': '{n} policies lapsed unpaid in the last quarter — value {amount}',
    'today.lapsedAction': 'Win-back list',
    'today.allClear': 'All clear',
    'today.call': 'Call',

    'collections.title': 'Collections',
    'collections.subtitle': 'Unpaid instalments across every policy — most overdue first',

    'notices.title': 'Renewal notices',
    'notices.subtitle': 'Who to ring about renewing — most urgent first',
    'notices.status': 'Notice',
    'notices.received': 'Notice issued',
    'notices.notifiedAt': 'Notified',
    'notices.note': 'What the client said',
    'notices.record': 'Record notification',
    'notices.markNotified': 'Notified',
    'notices.pendingCount': 'Calls outstanding',
    'notices.showDeclined': 'Include «not renewing»',
    'notices.empty': 'No outstanding renewal notices',
    'notices.hint':
      'The insurer issues a renewal notice before expiry. This is where you log the call to the client and what they answered.',

    'notices.email': 'Email',
    'notices.emailSend': 'Send renewal notice',
    'notices.emailSendShort': 'Send',
    'notices.emailResend': 'Send again',
    'notices.emailSent': 'Sent',
    'notices.emailNotSent': 'Not sent',
    'notices.emailSentAt': 'Emailed {date}',
    'notices.emailRecipient': 'Recipient',
    'notices.emailSubject': 'Subject',
    'notices.emailPreview': 'Preview',
    'notices.emailNoAddress': 'The client has no email address',
    'notices.emailDisabled': 'Renewal emails are switched off in Settings',
    'notices.emailSendDue': 'Send to everything due',
    'notices.emailSendDueHint':
      'Sends the notice to every policy expiring inside the configured window that has not been emailed yet.',
    'notices.emailSendSelected': 'Send email',
    'notices.emailBatchResult': '{sent} sent · {skipped} skipped',
    'notices.emailAutoNotice': 'Sent automatically {days} days before expiry',

    'settings.renewalEmail': 'Renewal notices by email',
    'settings.renewalEmailHint': 'The message a client receives before their policy expires.',
    'settings.renewalEmailEnabled': 'Enabled',
    'settings.renewalEmailEnabledHint':
      'While this is off no email leaves, automatically or by hand.',
    'settings.renewalEmailMode': 'How it is sent',
    'settings.renewalEmailModeAuto': 'Automatically',
    'settings.renewalEmailModeManual': 'By hand',
    'settings.renewalEmailModeAutoHint':
      'Goes out on its own each morning, to policies expiring inside the window below.',
    'settings.renewalEmailModeManualHint':
      'Nothing leaves on its own — you send from Renewal notices, per client or all at once.',
    'settings.renewalEmailLeadDays': 'Days before expiry',
    'settings.renewalEmailLeadDaysAuto': 'The email goes out this many days before the policy expires.',
    'settings.renewalEmailLeadDaysManual':
      'The same window decides what the “Send to everything due” button on Renewal notices sends.',
    'settings.renewalEmailSubject': 'Subject',
    'settings.renewalEmailBody': 'Message',
    'settings.renewalEmailSender': 'Sender name',
    'settings.renewalEmailReplyTo': 'Reply-to address',
    'settings.renewalEmailPlaceholders': 'Fields you can use',
    'settings.renewalEmailPlaceholdersHint':
      'Click one to insert it. Each is replaced with that policy’s own details.',
    'settings.renewalEmailTest': 'Send a test',
    'settings.renewalEmailTestHint': 'Sends the template, filled with sample data, to one address.',
    'settings.renewalEmailTestSent': 'Test email sent to {email}',
    'settings.renewalEmailNoSmtp':
      'No SMTP server is configured — messages are written to the log instead of being sent.',
    'settings.renewalEmailUpdatedBy': 'Last changed by {name}, {date}',
    'collections.installment': 'Instalment',
    'collections.outstanding': 'Total outstanding',

    'policy.cancelPreview': 'Cancellation refund (pro-rata)',
    'policy.periodUsed': 'Period used: {used} of {total} days',
    'policy.earnedPremium': 'Earned premium',
    'policy.refundDue': 'Refund due to the client',
    'policy.refundNotPaid': 'The period was not paid up — no refund arises',
    'policy.commissionClawback': 'Pro-rata commission clawback to the insurer',
    'policy.refund': 'Cancellation refund',
    'policy.bmClass': 'Bonus-Malus',

    'client.opportunities': 'Sales opportunities',
    'client.opportunitiesHint': 'Core lines without an active policy',
    'client.since': 'Client since',

    'offline.online': 'Online',
    'offline.offline': 'Offline',
    'offline.onlineHint': 'All actions are available.',
    'offline.offlineHint': 'You can browse data and capture notes.',
    'offline.readOnlyHint': 'Showing data from local storage.',
    'offline.allSynced': 'Everything synced',
    'offline.syncNow': 'Sync now',
    'offline.queued': 'Saved locally',
    'offline.pending': 'Pending',
    'offline.pendingHint': 'Will be sent as soon as the connection returns.',
    'offline.attempts': '{n} attempts',
    'offline.bannerTitle': 'Working offline',
    'offline.bannerHint': 'Reminders and notes are saved on this device.',
    'offline.bannerQueued': '{n} actions waiting to send',
    'offline.bannerQueuedOne': '1 action waiting to send',
    'offline.requiresConnection': 'Needs a connection',
    'offline.policyHint': 'Offline you can add reminders and notes only. Payments, policies and client details need a connection.',
    'offline.op.reminderCreate': 'New reminder',
    'offline.op.reminderComplete': 'Complete reminder',
    'offline.op.reminderReopen': 'Reopen reminder',
    'offline.op.activityCreate': 'Log communication',
    'offline.op.timelineCreate': 'Claim note',
    'offline.blocked.financial': 'it moves money',
    'offline.blocked.contract': 'it changes a policy or claim',
    'offline.blocked.personalData': 'it involves personal data',
    'offline.blocked.security': 'it affects system security',

    'error.notFound': 'Page not found',
    'error.notFoundHint': 'The link may have changed, or the record was deleted.',
    'error.forbidden': 'No access',
    'error.forbiddenHint': 'Your role does not permit viewing this page.',
    'error.generic': 'Something went wrong',

    'enum.role.admin': 'Administrator',
    'enum.role.agent': 'Agent',
    'enum.role.viewer': 'Viewer',

    'enum.clientType.individual': 'Individual',
    'enum.clientType.company': 'Company',

    'enum.insuranceType.auto': 'Motor',
    'enum.insuranceType.roadside_assistance': 'Roadside assistance',
    'enum.insuranceType.health': 'Health',
    'enum.insuranceType.property': 'Property',
    'enum.insuranceType.life': 'Life',
    'enum.insuranceType.travel': 'Travel',
    'enum.insuranceType.liability': 'Liability',
    'enum.insuranceType.marine': 'Marine',
    'enum.insuranceType.professional_indemnity': 'Professional indemnity',
    'enum.insuranceType.cyber': 'Cyber',
    'enum.insuranceType.other': 'Other',

    'enum.insurerDocumentType.terms': 'Terms',
    'enum.insurerDocumentType.contract': 'Contract',
    'enum.insurerDocumentType.commission_agreement': 'Commission agreement',
    'enum.insurerDocumentType.product_sheet': 'Product sheet',
    'enum.insurerDocumentType.price_list': 'Price list',
    'enum.insurerDocumentType.circular': 'Circular',
    'enum.insurerDocumentType.other': 'Other',

    'insurer.claimsPhone': 'Accident line',
    'insurer.roadsidePhone': 'Roadside assistance',
    'insurer.emergencyPhone': '24h service',
    'insurer.portalUrl': 'Carrier portal',
    'insurer.clientFacing': 'Numbers to give a client',
    'insurer.clientFacingHint':
      'These are what you read out to a client — not your own account contact at the carrier.',
    'insurer.documents': 'Terms & contracts',

    'enum.renewalNoticeStatus.pending': 'Not yet called',
    'enum.renewalNoticeStatus.notified': 'Notified',
    'enum.renewalNoticeStatus.confirmed': 'Will renew',
    'enum.renewalNoticeStatus.declined': 'Will not renew',

    'enum.frequency.monthly': 'Monthly',
    'enum.frequency.quarterly': 'Quarterly',
    'enum.frequency.semiannual': 'Semi-annual',
    'enum.frequency.annual': 'Annual',
    'enum.frequency.one_off': 'One-off',

    'enum.policyStatus.active': 'Active',
    'enum.policyStatus.pending': 'Pending',
    'enum.policyStatus.expired': 'Expired',
    'enum.policyStatus.cancelled': 'Cancelled',
    'enum.policyStatus.renewal_due': 'Renewal due',
    'enum.policyStatus.paid': 'Settled (one-off)',

    'enum.paymentStatus.unpaid': 'Unpaid',
    'enum.paymentStatus.partially_paid': 'Partially paid',
    'enum.paymentStatus.paid': 'Paid',

    'enum.claimStatus.open': 'Open',
    'enum.claimStatus.in_progress': 'In progress',
    'enum.claimStatus.waiting_documents': 'Awaiting documents',
    'enum.claimStatus.approved': 'Approved',
    'enum.claimStatus.rejected': 'Rejected',
    'enum.claimStatus.paid': 'Paid out',
    'enum.claimStatus.closed': 'Closed',

    'enum.reminderStatus.pending': 'Pending',
    'enum.reminderStatus.completed': 'Completed',
    'enum.reminderStatus.overdue': 'Overdue',

    'enum.priority.low': 'Low',
    'enum.priority.normal': 'Normal',
    'enum.priority.high': 'High',

    'enum.installmentStatus.pending': 'Pending',
    'enum.installmentStatus.paid': 'Paid',
    'enum.installmentStatus.overdue': 'Overdue',
    'enum.installmentStatus.cancelled': 'Cancelled',

    'enum.paymentMethod.cash': 'Cash',
    'enum.paymentMethod.card': 'Card',
    'enum.paymentMethod.bank_transfer': 'Bank transfer',
    'enum.paymentMethod.direct_debit': 'Direct debit',
    'enum.paymentMethod.cheque': 'Cheque',

    'enum.commissionStatus.expected': 'Expected',
    'enum.commissionStatus.statement_received': 'Statement received',
    'enum.commissionStatus.settled': 'Settled',
    'enum.commissionStatus.disputed': 'Disputed',

    'enum.settlementStatus.draft': 'Draft',
    'enum.settlementStatus.reconciling': 'Reconciling',
    'enum.settlementStatus.closed': 'Closed',

    'enum.endorsementType.coverage_change': 'Coverage change',
    'enum.endorsementType.premium_adjustment': 'Premium adjustment',
    'enum.endorsementType.vehicle_change': 'Vehicle change',
    'enum.endorsementType.driver_change': 'Driver change',
    'enum.endorsementType.address_change': 'Address change',
    'enum.endorsementType.frequency_change': 'Frequency change',
    'enum.endorsementType.suspension': 'Suspension',
    'enum.endorsementType.reinstatement': 'Reinstatement',
    'enum.endorsementType.other': 'Other',

    'enum.insuredItemType.vehicle': 'Vehicle',
    'enum.insuredItemType.property': 'Property',
    'enum.insuredItemType.person': 'Person',
    'enum.insuredItemType.vessel': 'Vessel',
    'enum.insuredItemType.other': 'Other',

    'enum.vehicleCategory.passenger_private': 'Private car',
    'enum.vehicleCategory.passenger_rental': 'Rental car',
    'enum.vehicleCategory.taxi': 'Taxi',
    'enum.vehicleCategory.truck_private': 'Private truck',
    'enum.vehicleCategory.truck_public': 'Public truck',
    'enum.vehicleCategory.agricultural': 'Agricultural',
    'enum.vehicleCategory.bus': 'Bus',
    'enum.vehicleCategory.motorcycle': 'Motorcycle',
    'enum.vehicleCategory.motorcycle_rental': 'Rental motorcycle',
    'enum.vehicleCategory.trailer': 'Trailer',
    'enum.vehicleCategory.tractor': 'Tractor',
    'enum.vehicleCategory.other': 'Other',

    'enum.clientDocumentType.id_card': 'ID card',
    'enum.clientDocumentType.passport': 'Passport',
    'enum.clientDocumentType.driving_license': 'Driving licence',
    'enum.clientDocumentType.vehicle_reg': 'Vehicle registration',
    'enum.clientDocumentType.company_doc': 'Company document',
    'enum.clientDocumentType.contract': 'Contract',
    'enum.clientDocumentType.other': 'Other',

    'enum.policyDocumentType.policy_contract': 'Policy contract',
    'enum.policyDocumentType.payment_notice': 'Payment notice',
    'enum.policyDocumentType.endorsement': 'Endorsement',
    'enum.policyDocumentType.green_card': 'Green card',
    'enum.policyDocumentType.proposal': 'Proposal',
    'enum.policyDocumentType.cancellation': 'Cancellation',
    'enum.policyDocumentType.other': 'Other',

    'enum.timelineAction.created': 'Created',
    'enum.timelineAction.status_changed': 'Status changed',
    'enum.timelineAction.document_uploaded': 'Document uploaded',
    'enum.timelineAction.note_added': 'Note added',
    'enum.timelineAction.contact_insurer': 'Contacted insurer',
    'enum.timelineAction.payout_received': 'Payout received',
    'enum.timelineAction.follow_up': 'Follow-up',

    'enum.activityType.call': 'Call',
    'enum.activityType.email': 'Email',
    'enum.activityType.meeting': 'Meeting',
    'enum.activityType.note': 'Note',
    'enum.activityType.sms': 'SMS',
    'enum.activityType.visit': 'Visit',

    'enum.direction.inbound': 'Inbound',
    'enum.direction.outbound': 'Outbound',

    'enum.auditAction.create': 'Create',
    'enum.auditAction.update': 'Update',
    'enum.auditAction.delete': 'Delete',
    'enum.auditAction.login': 'Sign in',
    'enum.auditAction.export': 'Export',
    'enum.auditAction.import': 'Import',
  },
} as const;

export type TranslationKey = keyof (typeof dictionaries)['el'];
export type Dictionary = Record<string, string>;
